/**
 * thumb-lists (2026-10-24) — 「관련 콘텐츠 목록 썸네일」 고르는 순서 «한 곳»(순수 함수 · DB 0 · 네트워크 0).
 *
 *   현장  : ① 공식 사진(licensedHeroPhoto — 상세 히어로·카드와 같은 판정) → ② AI 상상도(site_images visible · alt·캡션 필수 RULES#168) → ③ 현장 타일
 *   글    : ① 자체 사진 커버(ownPhotoSrc — 블로그 화이트리스트 · 글자 카드·위성 배제) → ② 연결 현장 이미지(위 순서) → ③ 현장 타일 또는 글 유형 타일
 *   소식  : 그 현장 이미지(위 순서) → 타일(site_news 에는 이미지 칼럼이 없다 · 외부 기사 사진 금지)
 *   게시글: posts.images(자체 호스트만) → 태그 현장 이미지 → 타일
 *
 * ⛔ 쓰지 않는 것: 글자 구운 카드(isBakedCardUrl · /media/og 합성 · /api/og* 런타임 생성), 외부 핫링크, 크롤 images jsonb,
 *    cover_image_url(현장), 위성(satellite_image_url · /img-sat · /media/images/satellite — 위성은 카드 줄(KdMediaRail) 전용 규칙 그대로),
 *    /_next/image.
 * 출력 주소는 전부 «자체 도메인 상대 경로»(/media/… · /img/…) — 모든 UA 같은 HTML.
 * 타일은 이미지 요청 0 인 CSS 칸(aria-hidden). 이미지가 깨지면 화면에서 같은 타일로 접힌다(Thumb 컴포넌트).
 */
import { licensedHeroPhoto, type DerivedSiteLike } from '@/lib/apt/detail-derived';
import { isBakedCardUrl, generatedThumbCaption, type GeneratedShot, type ResolvedSiteImage } from '@/lib/site-image/pure';
import { OPEN_SALE_STAGES, PIPELINE_STAGES } from '@/lib/apt/lifecycle-label';

/* ───────────────────────────── 모양 ───────────────────────────── */

/** 타일 톤 4종(+글). 분양 중·선착순 / 분양예정 / 재개발·재건축 / 기축·입주 / 글 전용. */
export type ThumbTone = 'sale' | 'plan' | 'redev' | 'done' | 'post';

export interface TileSpec {
  tone: ThumbTone;
  /** 작은 줄 — 시군구(구 이름만) 또는 글의 시군구. 없으면 null. */
  label: string | null;
  /** 큰 줄 — 세대수(「1,644」) 또는 글 유형(「분석 글」). 없으면 null. */
  main: string | null;
  /** main 이 숫자인가(48px 이하 칸은 숫자만 남긴다). */
  numeric: boolean;
}

export interface ThumbImage {
  /** 자체 도메인 상대 경로(/media/… · /img/…). */
  src: string;
  alt: string;
  /** AI 상상도면 고지 문구(figcaption · title). 공식·자체 사진은 null. */
  caption: string | null;
  ai: boolean;
  /** 원본 픽셀 크기(모르면 null — 렌더는 칸 크기로 width/height 를 낸다). */
  width: number | null;
  height: number | null;
  /** ui-align2 — 작은 판(withSmall)으로 바꿨을 때의 원본(넓은 칸이 <picture> media 로 고른다). 없으면 src 가 원본. */
  full?: { src: string; width: number | null; height: number | null } | null;
}

/** 목록 한 칸 — 이미지(없으면 null) + 깨질 때·없을 때 그릴 타일(언제나 있다). JSON 직렬화 가능(클라이언트 섬으로 넘긴다). */
export interface ThumbData {
  img: ThumbImage | null;
  tile: TileSpec;
}

/* ───────────────────────────── 금지 소스 판정 ───────────────────────────── */

const SITE_ORIGIN_RE = /^https?:\/\/(?:www\.)?kadeora\.app(?=\/)/i;
const STORAGE_PUBLIC_RE = /^https:\/\/(?:[a-z0-9]{20}|kadeora)\.supabase\.co\/storage\/v1\/object\/public\/(.+)$/i;

/** 글자 구운·합성·생성·위성 경로(상대 경로 기준). */
function forbiddenPath(rel: string): boolean {
  const p = rel.split(/[?#]/)[0].toLowerCase();
  if (p.startsWith('/api/') || p.startsWith('/_next/') || p.startsWith('/og-collage') || p.startsWith('/img-sat')) return true;
  if (/^\/media\/(?:[^/]+\/)?og\//.test(p)) return true; // /media/og/** 합성 카드(글자 구움) · /media/{bucket}/og/**
  if (p.includes('/satellite/')) return true; // /media/images/satellite/** 위성(목록 행 금지)
  if (isBakedCardUrl(p)) return true; // /images/card/ · /img/{id}/ch/card-|cafe-
  if (/\/ch\//.test(p) && p.startsWith('/img/')) return true; // 채널 변형본(제목띠·칩 구움) 전부
  return false;
}

/**
 * 자체 사진 주소 «한 판정» — 블로그 커버·게시글 사진·공식 사진 공용.
 *   사이트 상대(/media/… · /img/…) · kadeora.app 절대 · Supabase Storage public 직링크(→ /media/…)만 통과 → 상대 경로.
 *   외부 호스트·프로토콜 상대(//)·http 아닌 스킴·/api/og*·/media/og 합성·위성·글자 카드 → null.
 */
export function ownPhotoSrc(url: string | null | undefined): string | null {
  const s = String(url ?? '').trim();
  if (!s) return null;
  let rel: string | null = null;
  if (s.startsWith('/') && !s.startsWith('//')) rel = s;
  else if (SITE_ORIGIN_RE.test(s)) rel = s.replace(SITE_ORIGIN_RE, '');
  else {
    const m = STORAGE_PUBLIC_RE.exec(s.replace(/^http:\/\//i, 'https://'));
    if (m) rel = `/media/${m[1]}`;
  }
  if (!rel) return null;
  if (!(rel.startsWith('/media/') || rel.startsWith('/img/'))) return null;
  if (forbiddenPath(rel)) return null;
  return rel;
}

/** 생성 상상도 원본 경로인가(/img/{uuid}/S…) — 글 커버에 실린 VX 커버 판정. */
export const isAiImgPath = (rel: string | null | undefined) => !!rel && /^\/img\/[0-9a-f-]{36}\/(?:[^/?#]*--)?S[0-9]_/i.test(rel);

/* ───────────────────────────── 타일 ───────────────────────────── */

const SALE_EXTRA = ['model_house_open', 'special_supply', 'contract_signing', 'contract'];
const DONE = ['move_in_started', 'post_move_in', 'landmark_active', 'active_trade', 'resale', 'move_in', 'pre_move_in', 'move_in_ready'];
const PLAN = ['site_planning', 'pre_announcement'];

/** 단계 → 타일 톤. 모르는 단계는 'done'(회색 · 중립). */
export function tileToneOf(stage: string | null | undefined, siteType?: string | null): Exclude<ThumbTone, 'post'> {
  const s = String(stage ?? '').trim();
  if ((OPEN_SALE_STAGES as readonly string[]).includes(s) || SALE_EXTRA.includes(s)) return 'sale';
  if (DONE.includes(s)) return 'done';
  if ((PIPELINE_STAGES as readonly string[]).includes(s) || s === 'redevelopment_active') {
    // 청약 라인의 construction 은 「공사 중(분양 끝남)」 — 입주 계열로 본다(title-tail 단계군과 같은 갈래).
    return s === 'construction' && siteType === 'subscription' ? 'done' : 'redev';
  }
  if (PLAN.includes(s)) return siteType === 'redevelopment' ? 'redev' : 'plan';
  return 'done';
}

/** 「부산광역시 해운대구」·「창원시 성산구」 → 「해운대구」·「성산구」(구 이름만). 4글자 넘으면 자른다. 빈 값 null. */
export function shortSigungu(v: string | null | undefined, max = 4): string | null {
  const parts = String(v ?? '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return null;
  const last = parts[parts.length - 1];
  return last.length > max ? last.slice(0, max) : last;
}

/** 세대수 → 「1,644」. 0·음수·비수 null. */
export function unitsText(v: unknown): string | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' && v.trim() ? Number(v.replace(/,/g, '')) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.round(n).toLocaleString('ko-KR') : null;
}

export interface SiteTileInput {
  lifecycle_stage?: string | null;
  site_type?: string | null;
  sigungu?: string | null;
  /** 시군구가 없을 때 대신 쓸 지역(시도). */
  region?: string | null;
  units?: number | string | null;
}

export function siteTileOf(s: SiteTileInput | null | undefined): TileSpec {
  const main = unitsText(s?.units);
  return {
    tone: tileToneOf(s?.lifecycle_stage, s?.site_type),
    label: shortSigungu(s?.sigungu) ?? shortSigungu(s?.region),
    main,
    numeric: !!main,
  };
}

/** 글 유형 라벨(보이는 글자). 카테고리·cron_type 으로 판정 — 모르면 「분석 글」. */
export function postKindLabel(p: { category?: string | null; cron_type?: string | null; sub_category?: string | null } | null | undefined): string {
  const c = String(p?.category ?? '').toLowerCase();
  const t = String(p?.cron_type ?? '').toLowerCase();
  if (/brief/.test(t)) return '브리핑';
  if (c === 'redev') return '정비 분석';
  if (c === 'unsold') return '미분양';
  if (c === 'news' || /news/.test(t)) return '소식';
  if (c === 'board' || c === 'post') return '게시글';
  return '분석 글';
}

export function postTileOf(p: { kindLabel?: string | null; sigungu?: string | null; region?: string | null } | null | undefined): TileSpec {
  const main = String(p?.kindLabel ?? '').trim() || '분석 글';
  return { tone: 'post', label: shortSigungu(p?.sigungu) ?? shortSigungu(p?.region), main, numeric: false };
}

/**
 * 칸 크기별 보이는 글자 — 12px 하한 규칙(r21a)과 같은 이유로 작은 칸은 줄인다.
 *   한 변(작은 쪽) 48px 이하 → 큰 줄 하나(숫자 · 숫자가 없으면 라벨 · 그것도 없으면 빈칸). 그보다 크면 작은 줄 + 큰 줄.
 */
export function tileTextFor(t: TileSpec, w: number, h: number): { label: string | null; main: string | null } {
  const small = Math.min(w, h) <= 48 || w <= 48;
  if (small) {
    const main = t.numeric ? t.main : (t.label ?? t.main);
    return { label: null, main: main ?? null };
  }
  return { label: t.label, main: t.main };
}

/* ───────────────────────────── 현장 이미지 ───────────────────────────── */

/** 상상도 컷 1장 → 목록 썸네일 원본 경로. 4:3 webp 파생본(화면용) → 1:1 JPEG(thumb) 순. 같은 판정을 작은 판 크론도 쓴다. */
export function aiThumbSourceOf(shot: GeneratedShot | null | undefined): { path: string; width: number; height: number } | null {
  if (!shot) return null;
  const r = shot.hero4x3 ?? shot.thumb ?? null;
  const rel = r ? ownPhotoSrc(r.path || r.url) : null;
  return rel && r ? { path: rel, width: r.width, height: r.height } : null;
}

export interface SiteThumbInput extends SiteTileInput {
  name?: string | null;
  /** apt_sites 부분 행(hero_image_url · hero_image_license · hero_license_tier · lifecycle_stage). 공식 판정 입력. */
  site?: DerivedSiteLike | null;
  /** lib/site-image resolveSiteImages 결과(있으면). */
  resolved?: ResolvedSiteImage | null;
}

/** 현장 이미지 1장(공식 → 상상도). 없으면 null(호출부는 타일). 위성·카드 등급은 목록 행에 쓰지 않는다. */
export function siteImageOf(i: SiteThumbInput | null | undefined): ThumbImage | null {
  if (!i) return null;
  const name = String(i.name ?? '').replace(/\s+/g, ' ').trim();
  const official = (i.site ? licensedHeroPhoto(i.site) : '') || (i.resolved?.kind === 'official' ? i.resolved.officialUrl ?? '' : '');
  const off = ownPhotoSrc(official);
  if (off) return { src: off, alt: name ? `${name} 공식 이미지` : '현장 공식 이미지', caption: null, ai: false, width: null, height: null };
  const r = i.resolved;
  if (r?.kind === 'generated' && r.hero) {
    const src = aiThumbSourceOf(r.hero);
    const alt = String(r.hero.alt ?? '').trim();
    const caption = String(r.hero.caption ?? '').trim() || generatedThumbCaption(name);
    // RULES#168 — alt 없는 상상도는 내지 않는다(고유 alt 필수).
    if (src && alt) return { src: src.path, alt, caption, ai: true, width: src.width, height: src.height };
  }
  return null;
}

export function siteThumb(i: SiteThumbInput | null | undefined): ThumbData {
  return { img: siteImageOf(i), tile: siteTileOf(i) };
}

/* ───────────────────────────── 글 · 소식 · 게시글 ───────────────────────────── */

export interface PostThumbInput {
  title?: string | null;
  cover?: string | null;
  /** 커버 alt(blog_posts.image_alt). */
  coverAlt?: string | null;
  category?: string | null;
  cron_type?: string | null;
  sigungu?: string | null;
  region?: string | null;
  /** 연결 현장(있으면 ② 단계). */
  site?: SiteThumbInput | null;
}

/** 글 커버 → 이미지 1장(자체 사진만). /img 상상도 커버면 AI 고지 동반. */
export function postCoverImageOf(p: { title?: string | null; cover?: string | null; coverAlt?: string | null } | null | undefined): ThumbImage | null {
  const src = ownPhotoSrc(p?.cover);
  if (!src) return null;
  const title = String(p?.title ?? '').replace(/\s+/g, ' ').trim();
  const alt0 = String(p?.coverAlt ?? '').trim();
  if (isAiImgPath(src)) {
    return { src, alt: alt0 || `${title} 관련 현장 AI 상상도`.trim(), caption: generatedThumbCaption(''), ai: true, width: null, height: null };
  }
  return { src, alt: alt0 || title, caption: null, ai: false, width: null, height: null };
}

export function postThumb(p: PostThumbInput | null | undefined): ThumbData {
  const own = postCoverImageOf(p);
  const siteImg = own ? null : siteImageOf(p?.site ?? null);
  const hasSite = !!p?.site && (p.site.lifecycle_stage != null || p.site.units != null);
  const tile = hasSite
    ? siteTileOf({ ...p!.site!, sigungu: p!.site!.sigungu ?? p?.sigungu ?? null, region: p!.site!.region ?? p?.region ?? null })
    : postTileOf({ kindLabel: postKindLabel(p), sigungu: p?.sigungu, region: p?.region });
  return { img: own ?? siteImg, tile };
}

/**
 * blog-thumbs — 글 목록 한 행의 «연결 현장» 참조(순수). 1순위 apt_site_id → 언급 첫 현장(mentioned_site_ids[0]) → hub_apt_slug.
 *   id 는 uuid 꼴만(주소에 실리는 값 — 그 밖은 버린다). 둘 다 없으면 null.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export interface PostSiteRef { id: string | null; slug: string | null }
export function postSiteRef(p: { apt_site_id?: string | null; mentioned_site_ids?: readonly string[] | null; hub_apt_slug?: string | null } | null | undefined): PostSiteRef | null {
  const cand = [p?.apt_site_id, ...(Array.isArray(p?.mentioned_site_ids) ? p!.mentioned_site_ids!.slice(0, 1) : [])]
    .map((x) => String(x ?? '').trim().toLowerCase()).find((x) => UUID_RE.test(x)) ?? null;
  const slug = String(p?.hub_apt_slug ?? '').trim() || null;
  return cand || slug ? { id: cand, slug } : null;
}

/** 현장 썸네일 맵(키 `id:{uuid}` · `slug:{slug}`)에서 글의 연결 현장 한 칸. */
export function siteThumbForRef(map: Readonly<Record<string, ThumbData>> | null | undefined, ref: PostSiteRef | null | undefined): ThumbData | null {
  if (!map || !ref) return null;
  return (ref.id ? map[`id:${ref.id}`] : undefined) ?? (ref.slug ? map[`slug:${ref.slug}`] : undefined) ?? null;
}

/**
 * blog-thumbs — 글 한 행 썸네일(이미 고른 «현장 칸» 을 받는 판). 순서는 postThumb 와 같다:
 *   ① 자체 사진 커버(postCoverImageOf — 글자 카드·/api/og·외부·위성 배제) → ② 연결 현장 이미지(공식 → AI 상상도) → ③ 현장 타일 또는 글 유형 타일.
 */
export function postThumbFrom(p: PostThumbInput | null | undefined, site: ThumbData | null | undefined): ThumbData {
  const own = postCoverImageOf(p);
  const tile = site?.tile ?? postTileOf({ kindLabel: postKindLabel(p), sigungu: p?.sigungu, region: p?.region });
  return { img: own ?? site?.img ?? null, tile };
}

/** 블로그 목록 행 입력(v_blog_posts_listing · blog_posts 부분 행). */
export interface BlogRowLike {
  id?: string | number | null;
  title?: string | null;
  cover_image?: string | null;
  image_alt?: string | null;
  category?: string | null;
  cron_type?: string | null;
  apt_region?: string | null;
  apt_sigungu?: string | null;
  apt_site_id?: string | null;
  mentioned_site_ids?: readonly string[] | null;
  hub_apt_slug?: string | null;
}

/** L5 치환 커버(현장 상상도 · alt·고지 동반) — 현장 판정이 이미지를 못 골랐을 때만 쓰는 마지막 이미지 후보. */
export interface VxCoverLike { src?: string | null; alt?: string | null; caption?: string | null; width?: number | null; height?: number | null }

/**
 * 블로그 목록 행 한 칸(순수) — postThumbFrom(자체 사진 커버 → 연결 현장 이미지 → 타일) + 이미지가 없을 때만 L5 치환 커버(alt·고지가 «다» 있을 때).
 *   bodyImage = 본문 첫 저장 이미지(커버가 자체 사진이 아닐 때 후보 · 같은 ownPhotoSrc 판정).
 */
export function blogRowThumb(
  p: BlogRowLike,
  siteMap: Readonly<Record<string, ThumbData>> | null | undefined,
  opts: { title?: string | null; bodyImage?: string | null; vx?: VxCoverLike | null } = {},
): ThumbData {
  const cover = ownPhotoSrc(p.cover_image) ? p.cover_image! : (opts.bodyImage ?? null);
  const t = postThumbFrom({
    title: opts.title ?? p.title ?? '', cover, coverAlt: p.image_alt ?? null, category: p.category ?? null, cron_type: p.cron_type ?? null,
    sigungu: p.apt_sigungu ?? null, region: p.apt_region ?? null,
  }, siteThumbForRef(siteMap, postSiteRef(p)));
  if (t.img) return t;
  const v = opts.vx;
  const src = v ? ownPhotoSrc(v.src) : null;
  const alt = String(v?.alt ?? '').trim();
  const caption = String(v?.caption ?? '').trim();
  return src && alt && caption ? { ...t, img: { src, alt, caption, ai: true, width: v?.width ?? null, height: v?.height ?? null } } : t;
}

/** 게시글 — posts.images(자체 호스트만, 첫 장) → 태그 현장 이미지 → 타일. */
export function boardPostThumb(p: { title?: string | null; images?: unknown; site?: SiteThumbInput | null; sigungu?: string | null }): ThumbData {
  const list = Array.isArray(p.images) ? p.images : [];
  let own: ThumbImage | null = null;
  for (const x of list) {
    const u = typeof x === 'string' ? x : x && typeof x === 'object' ? String((x as Record<string, unknown>).url ?? '') : '';
    const src = ownPhotoSrc(u);
    if (src && !isAiImgPath(src)) { own = { src, alt: String(p.title ?? '').trim() || '게시글 사진', caption: null, ai: false, width: null, height: null }; break; }
  }
  const siteImg = own ? null : siteImageOf(p.site ?? null);
  const tile = p.site ? siteTileOf(p.site) : postTileOf({ kindLabel: '게시글', sigungu: p.sigungu });
  return { img: own ?? siteImg, tile };
}

/* ───────────────────────────── 작은 판(240px) ───────────────────────────── */

export interface SmallThumb { path: string; width: number; height: number }
export type SmallLookup = (src: string) => SmallThumb | null | undefined;

/** 작은 판 매핑 키 — 쿼리·해시를 뗀 상대 경로. */
export const smallKeyOf = (src: string) => String(src ?? '').split(/[?#]/)[0];

/** 작은 판이 있으면 그 주소로(원본 픽셀 크기도 작은 판 값 · 원본은 img.full 에 남긴다). 없으면 그대로(원본 · 렌더가 lazy·async·width/height). */
export function withSmall(t: ThumbData, lookup?: SmallLookup | null): ThumbData {
  if (!t.img || !lookup) return t;
  const s = lookup(smallKeyOf(t.img.src));
  const path = s ? ownPhotoSrc(s.path) : null;
  return s && path ? { ...t, img: { ...t.img, src: path, width: s.width, height: s.height, full: { src: t.img.src, width: t.img.width, height: t.img.height } } } : t;
}

/** Map(src_path → 작은 판) 을 lookup 으로. */
export function lookupFromMap(map: ReadonlyMap<string, SmallThumb> | null | undefined): SmallLookup | null {
  return map && map.size ? (src) => map.get(smallKeyOf(src)) ?? null : null;
}

/** slug 맵에서 찾고, 없으면 현장 타일(이미지 0). 표 행처럼 slug 가 apt_sites 와 안 맞을 수 있는 곳용. */
export function thumbOrTile(map: Readonly<Record<string, ThumbData>> | null | undefined, slug: string | null | undefined, tile: SiteTileInput): ThumbData {
  const hit = slug ? map?.[slug] : undefined;
  return hit ?? { img: null, tile: siteTileOf(tile) };
}
